// PROMPT-046: Session Management Utilities
// Handles 30-minute session timeout with activity tracking and warnings

class SessionManager {
  constructor() {
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.warningTimeout = 25 * 60 * 1000; // 25 minutes for warning
    this.lastActivity = Date.now();
    this.warningTimer = null;
    this.sessionTimer = null;
    this.warningShown = false;
    
    this.init();
  }

  init() {
    // Track user activity
    this.trackActivity();
    
    // Start timers
    this.startTimers();
    
    // Load last activity from localStorage
    const savedActivity = localStorage.getItem('lastActivity');
    if (savedActivity) {
      this.lastActivity = parseInt(savedActivity);
    }
  }

  trackActivity() {
    // Events that count as user activity
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 
      'touchstart', 'click', 'keydown', 'focus'
    ];

    const updateActivity = () => {
      this.lastActivity = Date.now();
      localStorage.setItem('lastActivity', this.lastActivity.toString());
      
      // Reset timers when activity is detected
      this.resetTimers();
      
      // Reset warning flag
      this.warningShown = false;
      window.sessionExpiredShown = false;
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Also track API calls as activity
    this.interceptAPICalls(updateActivity);
  }

  interceptAPICalls(updateActivity) {
    // Intercept fetch calls
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      updateActivity();
      return originalFetch.apply(window, args);
    };

    // Intercept XMLHttpRequest
    const originalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function() {
      const xhr = new originalXHR();
      const originalOpen = xhr.open;
      xhr.open = function() {
        updateActivity();
        return originalOpen.apply(this, arguments);
      };
      return xhr;
    };
  }

  startTimers() {
    this.clearTimers();
    
    // Warning timer (25 minutes)
    this.warningTimer = setTimeout(() => {
      this.showWarning();
    }, this.warningTimeout);

    // Session expiration timer (30 minutes)
    this.sessionTimer = setTimeout(() => {
      this.handleSessionExpiration();
    }, this.sessionTimeout);
  }

  resetTimers() {
    this.clearTimers();
    this.startTimers();
  }

  clearTimers() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
  }

  showWarning() {
    if (this.warningShown) return;
    
    this.warningShown = true;
    
    // Create and show custom warning modal
    this.createSessionWarningModal();
  }

  createSessionWarningModal() {
    // Remove any existing modal
    const existingModal = document.getElementById('session-warning-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal elements
    const modal = document.createElement('div');
    modal.id = 'session-warning-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 9999;
      font-family: system-ui, -apple-system, sans-serif;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: white;
      padding: 24px;
      border-radius: 8px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    `;

    const title = document.createElement('h3');
    title.textContent = 'Session Expiration Warning';
    title.style.cssText = `
      margin: 0 0 16px 0;
      color: #e53e3e;
      font-size: 18px;
    `;

    const message = document.createElement('p');
    message.textContent = 'Your session will expire in 5 minutes due to inactivity. Please save your work and refresh your session if needed.';
    message.style.cssText = `
      margin: 0 0 24px 0;
      color: #4a5568;
      line-height: 1.5;
    `;

    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      gap: 12px;
      justify-content: center;
    `;

    const continueButton = document.createElement('button');
    continueButton.textContent = 'Continue Session';
    continueButton.style.cssText = `
      background: #3182ce;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `;
    continueButton.onclick = () => {
      // User confirmed - reset timers
      this.lastActivity = Date.now();
      localStorage.setItem('lastActivity', this.lastActivity.toString());
      this.resetTimers();
      this.warningShown = false;
      modal.remove();
    };

    const logoutButton = document.createElement('button');
    logoutButton.textContent = 'Logout Now';
    logoutButton.style.cssText = `
      background: #e53e3e;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    `;
    logoutButton.onclick = () => {
      // User chose to logout
      modal.remove();
      this.handleSessionExpiration();
    };

    // Assemble modal
    buttonContainer.appendChild(continueButton);
    buttonContainer.appendChild(logoutButton);
    modalContent.appendChild(title);
    modalContent.appendChild(message);
    modalContent.appendChild(buttonContainer);
    modal.appendChild(modalContent);

    // Add to page
    document.body.appendChild(modal);

    // Auto-remove modal after 1 minute if no action
    setTimeout(() => {
      if (document.getElementById('session-warning-modal')) {
        modal.remove();
        this.warningShown = false;
      }
    }, 60000);
  }

  handleSessionExpiration() {
    // Clear all storage
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user');
    localStorage.removeItem('entities');
    localStorage.removeItem('selectedEntity');
    localStorage.removeItem('lastActivity');
    sessionStorage.clear();

    // Log expiration message
    console.log('Your session has expired due to inactivity. Please log in again.');

    // Redirect to login
    window.location.href = '/login';
  }

  getRemainingTime() {
    const now = Date.now();
    const elapsed = now - this.lastActivity;
    const remaining = this.sessionTimeout - elapsed;
    
    return {
      total: this.sessionTimeout,
      elapsed: elapsed,
      remaining: Math.max(0, remaining),
      percentage: Math.max(0, Math.min(100, (remaining / this.sessionTimeout) * 100))
    };
  }

  isSessionValid() {
    const remaining = this.getRemainingTime().remaining;
    return remaining > 0;
  }

  forceExpire() {
    this.handleSessionExpiration();
  }

  cleanup() {
    this.clearTimers();
    
    // Remove event listeners
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 
      'touchstart', 'click', 'keydown', 'focus'
    ];

    activityEvents.forEach(event => {
      document.removeEventListener(event, this.updateActivity, true);
    });
  }
}

// Create and export singleton instance
let sessionManagerInstance = null;

export const getSessionManager = () => {
  if (!sessionManagerInstance) {
    sessionManagerInstance = new SessionManager();
  }
  return sessionManagerInstance;
};

export const sessionManager = getSessionManager();

export default sessionManager;
